import {Component, DestroyRef, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest, of, Subject} from 'rxjs';
import {catchError, debounceTime, filter, first, switchMap} from 'rxjs/operators';
import {AppStateService} from '../../services/app-state.service';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import {OrderService} from '../../services/order.service';
import {TranslateService} from '../../services/translate.service';
import {MarketingInfo, Order, PrebookInvite} from '../../models/Order';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {PlainButtonComponent} from '../buttons/plain-button.component';
import {CheckboxLabelComponent} from '../ui/checkbox-label.component';
import {TimeUtils} from '../../utils/Utils';
import {bookingMarketing, OFFERS_CONSENT, OFFERS_DECLINE, OFFERS_NOTICE, offersTickedAtStart} from '../../utils/marketing';
import {loadStoredContact, storeContact} from '../../utils/stored-contact';

/**
 * Entry route for a shared prebooking's invite link: /:venue_id/bj/:token. A guest of the party gives their name,
 * mobile number and e-mail (they join the venue's guest register, with their answer about offers as the online
 * booking asks it) and orders their own part; one who has joined before (this browser, or signed in) and the booker go straight to
 * the menu. The booker sends the whole party's order.
 */
@Component({
  standalone: true,
  selector: 'app-prebook-join',
  imports: [CommonModule, FormsModule, ActionButtonComponent, PlainButtonComponent, CheckboxLabelComponent],
  template: `
    <div class="max-w-lg mx-auto min-h-[calc(100vh)] pt-32 px-8 text-theme-text">
      @if (state === 'loading') {
        <div class="text-center">
          <i class="fas fa-circle-notch fa-spin text-3xl text-theme-primary"></i>
        </div>
      }
      @if (state === 'error') {
        <div class="text-center">
          <i class="fas fa-triangle-exclamation text-3xl text-theme-notification2"></i>
          <div class="mt-4 font-bold">{{ getString("Länken kunde inte öppnas") }}</div>
          <div class="mt-2 text-theme-text2">{{ getString("Inbjudan är ogiltig. Be den som bokade om en ny länk.") }}</div>
        </div>
      }
      @if (state === 'name' && invite) {
        <i class="fas fa-calendar-check text-3xl text-theme-primary"></i>
        <div class="mt-4 text-2xl font-bold">{{ invitedText() }}</div>
        <div class="mt-2 text-theme-text2">{{ bookingSummary() }}</div>
        @if (invite.lock_reason) {
          <div class="mt-6 text-theme-text2">{{ getString("Det går inte längre att förbeställa till den här bokningen.") }}</div>
          <div class="mt-8">
            <app-plain-button [text]="getString('Till startsidan')" bgColor="bg-theme-primary" color="text-theme-contrast" [customClass]="'h-14'"
                              (onClick)="navStart()"></app-plain-button>
          </div>
        } @else {
          <div class="mt-6 text-theme-text2">{{ getString("Välj din mat, så skickar den som bokade in hela sällskapets beställning.") }}
            {{ getString("Beställ senast") }} {{ deadline() }}.</div>
          <div class="mt-6 font-bold mb-1">{{ getString("Ditt namn") }}</div>
          <input class="text-input" [(ngModel)]="name" (ngModelChange)="contactChanged()" autocomplete="name">
          <div class="mt-4 font-bold mb-1">{{ getString("Mobilnummer") }}</div>
          <input type="tel" class="text-input" [(ngModel)]="phone" (ngModelChange)="contactChanged()" autocomplete="tel" placeholder="070-123 45 67">
          <div class="mt-4 font-bold mb-1">{{ getString("E-post") }}</div>
          <input type="email" class="text-input" [(ngModel)]="email" (ngModelChange)="contactChanged()" autocomplete="email" placeholder="namn@exempel.se">
          <!-- Offers by e-mail and SMS, as the online booking asks: the notice with a box for no thanks, or a box for a
               yes (samwise says which, and whether the guest already had the choice) -->
          @if (marketingInfo?.question) {
            <div class="mt-6 text-sm">
              @if (marketingInfo!.customer_relationship) {
                <p class="text-theme-text2">{{ offersNotice() }}</p>
              }
              <!-- The box is an inline element: a block of its own keeps it off the text above -->
              <div [class.mt-4]="marketingInfo!.customer_relationship">
                <app-checkbox-label [(ngModel)]="offersTicked">
                  <span>{{ offersBox() }}</span>
                </app-checkbox-label>
              </div>
            </div>
          }
          @if (refused) {
            <div class="mt-4 text-theme-notification2">{{ getString("Kontrollera ditt namn, mobilnummer och e-post.") }}</div>
          }
          <!-- relative: the button's spinner is placed against it -->
          <div class="mt-6 relative">
            <app-action-button [text]="getString('Välj din mat')" [disabled]="!contactGiven()" [spinner]="working" (onClick)="join()"></app-action-button>
          </div>
        }
      }
    </div>
  `
})
export class PrebookJoinComponent implements OnInit {
  state: 'loading' | 'error' | 'name' = 'loading';
  invite?: PrebookInvite;
  name = '';
  phone = '';
  email = '';
  // How to ask about offers for the contact info given; no box until samwise answers, or when it fails
  marketingInfo?: MarketingInfo;
  offersTicked = false;
  working = false;
  // The server refused the contact info
  refused = false;
  private token = '';
  private contact$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router, private appState: AppStateService,
              private orderService: OrderService, private api: AuthorizedApiService,
              private translate: TranslateService, private destroyRef: DestroyRef) { }

  ngOnInit(): void {
    // Asked once the details look right, a moment after the guest stops typing
    this.contact$.pipe(
      debounceTime(400),
      filter(() => this.contactGiven()),
      switchMap(() => this.api.marketingInfo(this.appState.venueId ?? '', this.name.trim(), this.phone.trim(), this.email.trim())
        .pipe(catchError(() => of(undefined)))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(info => {
      if (info?.question !== this.marketingInfo?.question) {
        this.offersTicked = offersTickedAtStart(info);
      }
      this.marketingInfo = info;
    });

    combineLatest([
      this.route.paramMap,
      this.appState.observeAppState()
    ]).pipe(first()).subscribe(([params, appState]) => {
      const stored = loadStoredContact();
      const userCfg = appState.order?.user_cfg;
      const signedIn = !(userCfg?.is_anon ?? true);
      this.name = stored?.name || (signedIn ? userCfg?.user_name ?? '' : '');
      this.phone = stored?.phone || (signedIn ? userCfg?.phone || userCfg?.auth_phone || '' : '');
      this.email = stored?.email || (signedIn ? userCfg?.email ?? '' : '');
      this.contactChanged();
      this.token = params.get("token") ?? '';
      this.orderService.prebookJoin(this.token).subscribe({
        next: (res) => this.land(res),
        error: (e) => {
          console.error("prebook_join failed", e);
          this.state = 'error';
        }
      });
    });
  }

  contactChanged() {
    this.refused = false;
    this.contact$.next();
  }

  contactGiven(): boolean {
    return !!this.name.trim() && /^\+?[\d\s-]{6,}$/.test(this.phone.trim()) && /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(this.email.trim());
  }

  join() {
    if (!this.contactGiven() || this.working) {
      return;
    }
    this.working = true;
    const contact = {name: this.name.trim(), phone: this.phone.trim(), email: this.email.trim()};
    storeContact(contact);
    const marketing = bookingMarketing(this.marketingInfo, this.offersTicked, this.offersNotice(), this.offersBox());
    this.orderService.prebookJoin(this.token, {...contact, marketing}).subscribe({
      next: (res) => this.land(res),
      error: (e) => {
        console.error("prebook_join failed", e);
        this.working = false;
        // The link was fine a moment ago: what was refused is the contact info, unless the prebooking just closed
        if (e?.status === 422 && e?.error?.error !== "INVALID_PREBOOK_INVITE") {
          this.refused = true;
          return;
        }
        this.state = 'error';
      }
    });
  }

  offersNotice(): string {
    return this.getString(OFFERS_NOTICE).replace('%s', this.marketingInfo?.marketing_name ?? '');
  }

  offersBox(): string {
    return this.marketingInfo?.customer_relationship
      ? this.getString(OFFERS_DECLINE)
      : this.getString(OFFERS_CONSENT).replace('%s', this.marketingInfo?.marketing_name ?? '');
  }

  // Joined (or the booker): the first guest menu, or the booking once it is sent; else the name page
  private land(res: Order & {invite?: PrebookInvite}) {
    if (res.invite) {
      this.invite = res.invite;
      this.state = 'name';
      return;
    }
    const bookedRef = res.prebook?.booked?.booking_ref;
    if (bookedRef) {
      this.router.navigate([this.appState.venueId, "booking", bookedRef], { replaceUrl: true });
      return;
    }
    const pageId = res.prebook?.template.menus?.[0]?.id ?? "food";
    this.router.navigate([this.appState.venueId, "menu", pageId], { replaceUrl: true });
  }

  invitedText(): string {
    const booker = this.invite?.booker_name;
    return booker ? `${booker} ${this.getString("bjuder in dig att förbeställa")}` : this.getString("Du är inbjuden att förbeställa");
  }

  bookingSummary(): string {
    const parts = [this.getString("Bokning") + " #" + this.invite!.external_ref];
    if (this.invite!.time_local) {
      // "2026-08-15T18:00" -> "2026-08-15 18:00"
      parts.push(this.invite!.time_local.substring(0, 16).replace("T", " "));
    }
    if (this.invite!.pax) {
      parts.push(this.invite!.pax + " " + this.getString("gäster"));
    }
    return parts.join(" · ");
  }

  deadline(): string {
    return TimeUtils.formatDeadlineClock(this.invite!.edit_lock_at);
  }

  navStart() {
    this.router.navigate([this.appState.venueId, "start"]);
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }
}
